import React, { useState, useEffect } from 'react';
import { Save, TestTube, Eye, EyeOff, TrendingUp, Users, DollarSign } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import apiClient from '../../utils/api';
import './ChatbotSettings.css';

const MODELS = [
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Günstig)', color: '#10b981' },
  { value: 'gpt-4o', label: 'GPT-4o (Schnell)', color: '#3b82f6' },
  { value: 'gpt-4', label: 'GPT-4 (Beste Qualität)', color: '#8b5cf6' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Sehr günstig)', color: '#6366f1' }
];

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

function ChatbotSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [userStats, setUserStats] = useState([]);
  
  // Form state
  const [companionName, setCompanionName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [proactiveFrequency, setProactiveFrequency] = useState(2);
  const [coinsPerMessage, setCoinsPerMessage] = useState(1);
  const [requestLimitStandard, setRequestLimitStandard] = useState(20);
  const [requestLimitPremium, setRequestLimitPremium] = useState(100);
  const [contextSizeJournal, setContextSizeJournal] = useState(20);
  const [contextSizeActivations, setContextSizeActivations] = useState(20);
  const [openaiModel, setOpenaiModel] = useState('gpt-4o-mini');
  const [adminApiKey, setAdminApiKey] = useState('');
  const [useAdminKey, setUseAdminKey] = useState(false);

  useEffect(() => {
    loadSettings();
    loadUsageStats();
    loadUserStats();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await apiClient.getChatbotSettings();
      setSettings(data);
      setCompanionName(data.companion_name);
      setSystemPrompt(data.system_prompt);
      setProactiveFrequency(data.proactive_frequency);
      setCoinsPerMessage(data.coins_per_message || 1);
      setRequestLimitStandard(data.request_limit_standard);
      setRequestLimitPremium(data.request_limit_premium);
      setContextSizeJournal(data.context_size_journal);
      setContextSizeActivations(data.context_size_activations);
      setOpenaiModel(data.openai_model);
      setUseAdminKey(data.use_admin_key);
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const loadUsageStats = async () => {
    try {
      const data = await apiClient.getChatbotUsageStats();
      setUsageStats(data);
    } catch (error) {
      console.error('Error loading usage stats:', error);
    }
  };

  const loadUserStats = async () => {
    try {
      const data = await apiClient.getChatbotUsageByUser();
      setUserStats(data.users || []);
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const updates = {
        companion_name: companionName,
        system_prompt: systemPrompt,
        proactive_frequency: proactiveFrequency,
        coins_per_message: coinsPerMessage,
        request_limit_standard: requestLimitStandard,
        request_limit_premium: requestLimitPremium,
        context_size_journal: contextSizeJournal,
        context_size_activations: contextSizeActivations,
        openai_model: openaiModel,
        use_admin_key: useAdminKey
      };
      
      if (adminApiKey) {
        updates.admin_api_key = adminApiKey;
      }
      
      await apiClient.updateChatbotSettings(updates);
      alert('Einstellungen gespeichert!');
      loadSettings();
      setAdminApiKey(''); // Clear after save
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(`Fehler: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiClient.testChatbotConnection();
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="chatbot-settings-loading">Lade Einstellungen...</div>;
  }

  return (
    <div className="chatbot-settings" data-testid="chatbot-settings">
      <div className="chatbot-settings-header">
        <h2>🤖 AI-Begleiter Verwaltung</h2>
        <p>Konfiguriere den ZENTA-Begleiter und überwache die Kosten</p>
      </div>

      <div className="chatbot-settings-grid">
        {/* Configuration Section */}
        <div className="chatbot-settings-card">
          <h3 className="card-title">Persönlichkeit & Verhalten</h3>
          
          <div className="form-group">
            <label>Name des Begleiters</label>
            <input
              type="text"
              value={companionName}
              onChange={(e) => setCompanionName(e.target.value)}
              data-testid="companion-name-input"
            />
          </div>

          <div className="form-group">
            <label>System-Prompt (Persönlichkeit)</label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={10}
              data-testid="system-prompt-input"
            />
            <span className="form-hint">Definiert die Persönlichkeit und das Verhalten des Begleiters</span>
          </div>

          <div className="form-group">
            <label>Proaktive Nachrichten pro Tag: {proactiveFrequency}</label>
            <input
              type="range"
              min="0"
              max="5"
              value={proactiveFrequency}
              onChange={(e) => setProactiveFrequency(parseInt(e.target.value))}
              data-testid="proactive-frequency-slider"
            />
            <span className="form-hint">0 = deaktiviert, 5 = sehr häufig</span>
          </div>
        </div>

        {/* API Configuration */}
        <div className="chatbot-settings-card">
          <h3 className="card-title">OpenAI API Konfiguration</h3>
          
          <div className="form-group">
            <label>Modell</label>
            <select
              value={openaiModel}
              onChange={(e) => setOpenaiModel(e.target.value)}
              data-testid="model-select"
            >
              {MODELS.map(model => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Eigener API-Key (optional)</label>
            <div className="api-key-input-group">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={adminApiKey}
                onChange={(e) => setAdminApiKey(e.target.value)}
                placeholder={settings?.has_admin_key ? 'Key hinterlegt (neu eingeben zum Ändern)' : 'Emergent LLM Key wird verwendet'}
                data-testid="admin-api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="api-key-toggle"
                data-testid="toggle-api-key-visibility"
              >
                {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <span className="form-hint">
              {settings?.has_admin_key ? '✅ Eigener Key hinterlegt' : 'ℹ️ Emergent LLM Key wird verwendet (Fallback)'}
            </span>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={useAdminKey}
                onChange={(e) => setUseAdminKey(e.target.checked)}
                data-testid="use-admin-key-checkbox"
              />
              <span>Eigenen API-Key verwenden (wenn hinterlegt)</span>
            </label>
          </div>

          <button
            onClick={handleTest}
            disabled={testing}
            className="test-button"
            data-testid="test-connection-button"
          >
            <TestTube size={18} />
            {testing ? 'Teste...' : 'Verbindung testen'}
          </button>

          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`} data-testid="test-result">
              <strong>{testResult.success ? '✅ Erfolgreich!' : '❌ Fehler'}</strong>
              <p>{testResult.message}</p>
              {testResult.response && (
                <p className="test-response">„{testResult.response}“</p>
              )}
            </div>
          )}
        </div>

        {/* Limits Configuration */}
        <div className="chatbot-settings-card">
          <h3 className="card-title">Coins & Context</h3>
          
          <div className="form-group">
            <label>Coins pro Nachricht: {coinsPerMessage} 💰</label>
            <input
              type="number"
              value={coinsPerMessage}
              onChange={(e) => setCoinsPerMessage(parseInt(e.target.value))}
              min="1"
              max="100"
              data-testid="coins-per-message-input"
            />
            <small style={{color: '#888', display: 'block', marginTop: '5px'}}>
              Jede KI-Chat-Nachricht kostet diese Anzahl an Coins
            </small>
          </div>

          <div className="form-group" style={{opacity: 0.5}}>
            <label>Request-Limit (Standard): {requestLimitStandard}/Tag [VERALTET]</label>
            <input
              type="number"
              value={requestLimitStandard}
              onChange={(e) => setRequestLimitStandard(parseInt(e.target.value))}
              min="1"
              max="200"
              disabled
              data-testid="request-limit-standard-input"
            />
            <small style={{color: '#888', display: 'block', marginTop: '5px'}}>
              ⚠️ Wird durch Coin-System ersetzt
            </small>
          </div>

          <div className="form-group" style={{opacity: 0.5}}>
            <label>Request-Limit (Premium): {requestLimitPremium}/Tag [VERALTET]</label>
            <input
              type="number"
              value={requestLimitPremium}
              onChange={(e) => setRequestLimitPremium(parseInt(e.target.value))}
              min="1"
              max="500"
              disabled
              data-testid="request-limit-premium-input"
            />
            <small style={{color: '#888', display: 'block', marginTop: '5px'}}>
              ⚠️ Wird durch Coin-System ersetzt
            </small>
          </div>

          <div className="form-group">
            <label>Context: Journal-Einträge: {contextSizeJournal}</label>
            <input
              type="number"
              value={contextSizeJournal}
              onChange={(e) => setContextSizeJournal(parseInt(e.target.value))}
              min="5"
              max="50"
              data-testid="context-journal-input"
            />
          </div>

          <div className="form-group">
            <label>Context: Aktivierungen: {contextSizeActivations}</label>
            <input
              type="number"
              value={contextSizeActivations}
              onChange={(e) => setContextSizeActivations(parseInt(e.target.value))}
              min="5"
              max="50"
              data-testid="context-activations-input"
            />
          </div>
        </div>

        {/* Usage Stats */}
        {usageStats && (
          <>
            <div className="chatbot-settings-card">
              <h3 className="card-title">
                <DollarSign size={20} />
                Kosten-Übersicht (Aktueller Monat)
              </h3>
              
              <div className="stats-grid">
                <div className="stat-box">
                  <div className="stat-label">Gesamt-Kosten</div>
                  <div className="stat-value">${usageStats.total_cost_usd.toFixed(4)}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Anfragen</div>
                  <div className="stat-value">{usageStats.total_requests}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Tokens</div>
                  <div className="stat-value">{usageStats.total_tokens.toLocaleString()}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Ø Kosten/Anfrage</div>
                  <div className="stat-value">${usageStats.average_cost_per_request.toFixed(6)}</div>
                </div>
              </div>

              {usageStats.model_costs && Object.keys(usageStats.model_costs).length > 0 && (
                <div className="model-costs">
                  <h4>Kosten nach Modell:</h4>
                  {Object.entries(usageStats.model_costs).map(([model, cost]) => (
                    <div key={model} className="model-cost-row">
                      <span>{model}</span>
                      <span>${cost.toFixed(4)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="chatbot-settings-card chart-card">
              <h3 className="card-title">
                <TrendingUp size={20} />
                Nutzung (Letzte 30 Tage)
              </h3>
              {usageStats.daily_stats && usageStats.daily_stats.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={usageStats.daily_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis
                      dataKey="date"
                      stroke="rgba(255,255,255,0.5)"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}.${date.getMonth() + 1}`;
                      }}
                    />
                    <YAxis stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        background: '#1a1a2e',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString('de-DE')}
                    />
                    <Line
                      type="monotone"
                      dataKey="requests"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={false}
                      name="Anfragen"
                    />
                    <Line
                      type="monotone"
                      dataKey="cost_usd"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                      name="Kosten ($)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="no-data">Keine Daten verfügbar</p>
              )}
            </div>
          </>
        )}

        {/* User Stats */}
        {userStats.length > 0 && (
          <div className="chatbot-settings-card user-stats-card">
            <h3 className="card-title">
              <Users size={20} />
              Nutzung pro User (Top 10)
            </h3>
            <div className="user-stats-table">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Anfragen</th>
                    <th>Tokens</th>
                    <th>Kosten</th>
                  </tr>
                </thead>
                <tbody>
                  {userStats.slice(0, 10).map(user => (
                    <tr key={user.user_id}>
                      <td>{user.email}</td>
                      <td>{user.requests}</td>
                      <td>{user.tokens.toLocaleString()}</td>
                      <td>${user.cost_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="chatbot-settings-actions">
        <button
          onClick={handleSave}
          disabled={saving}
          className="save-button"
          data-testid="save-settings-button"
        >
          <Save size={20} />
          {saving ? 'Speichere...' : 'Einstellungen speichern'}
        </button>
      </div>
    </div>
  );
}

export default ChatbotSettings;